/**
 * RECOMMENDATION ENGINE - Phase 3 Adaptive Intelligence
 * Adjusts difficulty based on student performance
 */
import { store } from './store.js';

const RecommendationEngine = {
    /**
     * Internal state
     */
    state: {
        consecutiveFailures: 0,
        consecutiveSuccesses: 0,
        totalAttempts: 0,
        correctAttempts: 0,
        errorHistory: [],  // Last 10 errors for pattern detection
        lastRecommendation: null,
        sessionStartTime: null
    },

    /**
     * Thresholds for recommendations
     */
    THRESHOLDS: {
        FAILURES_FOR_EASIER: 2,      // After 2 consecutive failures, suggest easier
        SUCCESSES_FOR_HARDER: 5,     // After 5 consecutive successes, suggest harder
        SIMILAR_ERRORS_FOR_TUTORIAL: 3  // After 3 similar errors, suggest tutorial
    },

    /**
     * Recommendation types
     */
    RECOMMENDATION_TYPES: {
        EASIER: 'easier',
        HARDER: 'harder',
        SAME: 'same',
        TUTORIAL: 'tutorial'
    },

    /**
     * Initialize the recommendation engine
     */
    init() {
        this.state.sessionStartTime = Date.now();
        console.log('🎯 RecommendationEngine initialized');
    },

    /**
     * Record a problem attempt result
     * @param {string} problemType - Type of problem ('suma' or 'resta')
     * @param {boolean} isCorrect - Whether the answer was correct
     * @param {string} errorType - Type of error if incorrect (from SmartFeedback)
     */
    recordAttempt(problemType, isCorrect, errorType = null) {
        this.state.totalAttempts++;

        if (isCorrect) {
            this.state.correctAttempts++;
            this.state.consecutiveSuccesses++;
            this.state.consecutiveFailures = 0;

            // Check for streak message
            if (this.state.consecutiveSuccesses >= this.THRESHOLDS.SUCCESSES_FOR_HARDER) {
                this.showStreakMessage('success', this.state.consecutiveSuccesses);
            }

            // Award Gamification Bonus
            if (window.GamificationService) {
                window.GamificationService.awardStreakBonus(this.state.consecutiveSuccesses);
            }
        } else {
            this.state.consecutiveFailures++;
            this.state.consecutiveSuccesses = 0;

            // Track error for pattern detection
            if (errorType) {
                this.recordError(errorType);
            }

            // Check for struggle message
            if (this.state.consecutiveFailures >= this.THRESHOLDS.FAILURES_FOR_EASIER) {
                this.showStruggleMessage();
            }
        }

        // Update gamification if available
        if (window.GamificationService && isCorrect) {
            // This is handled by phase2Integration
        }
    },

    /**
     * Record an error type for pattern detection
     * @param {string} errorType - Error type from SmartFeedback
     */
    recordError(errorType) {
        this.state.errorHistory.push({
            type: errorType,
            timestamp: Date.now()
        });

        // Keep only last 10 errors
        if (this.state.errorHistory.length > 10) {
            this.state.errorHistory.shift();
        }

        // Check for repeated error pattern
        this.checkErrorPattern();
    },

    /**
     * Check for repeated error patterns
     */
    checkErrorPattern() {
        if (this.state.errorHistory.length < this.THRESHOLDS.SIMILAR_ERRORS_FOR_TUTORIAL) {
            return;
        }

        // Get last N errors
        const recentErrors = this.state.errorHistory.slice(-this.THRESHOLDS.SIMILAR_ERRORS_FOR_TUTORIAL);
        const errorTypes = recentErrors.map(e => e.type);

        // Check if all recent errors are the same type
        const allSame = errorTypes.every(t => t === errorTypes[0]);

        if (allSame && errorTypes[0] !== 'unknown') {
            this.showTutorialSuggestion(errorTypes[0]);
        }
    },

    /**
     * Get current recommendation based on performance
     * @returns {Object} Recommendation { type, message, action }
     */
    getRecommendation() {
        // Tutorial recommendation (highest priority)
        if (this.shouldSuggestTutorial()) {
            return {
                type: this.RECOMMENDATION_TYPES.TUTORIAL,
                message: this.getTutorialMessage(),
                action: 'showTutorial'
            };
        }

        // Easier recommendation
        if (this.state.consecutiveFailures >= this.THRESHOLDS.FAILURES_FOR_EASIER) {
            return {
                type: this.RECOMMENDATION_TYPES.EASIER,
                message: '¿Vamos con algo más sencillo? ¡Así cogemos impulso!',
                action: 'reduceDifficulty'
            };
        }

        // Harder recommendation
        if (this.state.consecutiveSuccesses >= this.THRESHOLDS.SUCCESSES_FOR_HARDER) {
            return {
                type: this.RECOMMENDATION_TYPES.HARDER,
                message: `¡Racha de ${this.state.consecutiveSuccesses}! 🔥 ¿Probamos algo más difícil?`,
                action: 'increaseDifficulty'
            };
        }

        // Same difficulty
        return {
            type: this.RECOMMENDATION_TYPES.SAME,
            message: null,
            action: null
        };
    },

    /**
     * Check if tutorial should be suggested
     */
    shouldSuggestTutorial() {
        if (this.state.errorHistory.length < this.THRESHOLDS.SIMILAR_ERRORS_FOR_TUTORIAL) {
            return false;
        }

        const recentErrors = this.state.errorHistory.slice(-this.THRESHOLDS.SIMILAR_ERRORS_FOR_TUTORIAL);
        const errorTypes = recentErrors.map(e => e.type);

        return errorTypes.every(t => t === errorTypes[0]) && errorTypes[0] !== 'unknown';
    },

    /**
     * Get appropriate tutorial message based on error pattern
     */
    getTutorialMessage() {
        if (this.state.errorHistory.length === 0) return null;

        const errorType = this.state.errorHistory[this.state.errorHistory.length - 1].type;

        const messages = {
            'carrying_error': '¿Practicamos las llevadas? Parece que te cuestan un poquito.',
            'borrowing_error': '¿Repasamos los préstamos en las restas? ¡Es fácil cuando le coges el truco!',
            'operation_confusion_sum': '¿Repasamos cuándo hay que sumar y cuándo restar?',
            'operation_confusion_sub': '¿Repasamos cuándo hay que sumar y cuándo restar?',
            'digit_swap': 'Recuerda: las decenas van a la izquierda, las unidades a la derecha.'
        };

        return messages[errorType] || '¿Probamos con un ejercicio de práctica?';
    },

    /**
     * Show streak message (success streak)
     * @param {string} type - 'success' or 'failure'
     * @param {number} count - Streak count
     */
    showStreakMessage(type, count) {
        // Create streak notification
        const notification = document.createElement('div');
        notification.className = 'streak-notification success-streak';
        notification.innerHTML = `
            <div class="streak-icon">🔥</div>
            <div class="streak-text">
                <div class="streak-title">¡RACHA DE ${count}!</div>
                <div class="streak-subtitle">¿Probamos algo más difícil?</div>
            </div>
            <div class="streak-actions">
                <button class="btn btn-primary btn-small" onclick="RecommendationEngine.acceptHarderChallenge()">
                    ¡Sí!
                </button>
                <button class="btn btn-secondary btn-small" onclick="RecommendationEngine.dismissNotification(this)">
                    Ahora no
                </button>
            </div>
        `;

        this.showNotification(notification);
    },

    /**
     * Show struggle message (failure streak)
     */
    showStruggleMessage() {
        // Don't show too frequently
        if (this.lastStruggleTime && Date.now() - this.lastStruggleTime < 60000) {
            return;
        }
        this.lastStruggleTime = Date.now();

        const notification = document.createElement('div');
        notification.className = 'streak-notification struggle-notification';
        notification.innerHTML = `
            <div class="streak-icon">💪</div>
            <div class="streak-text">
                <div class="streak-title">¡No te rindas!</div>
                <div class="streak-subtitle">¿Quieres probar algo más fácil?</div>
            </div>
            <div class="streak-actions">
                <button class="btn btn-primary btn-small" onclick="RecommendationEngine.acceptEasierChallenge()">
                    Sí, por favor
                </button>
                <button class="btn btn-secondary btn-small" onclick="RecommendationEngine.dismissNotification(this)">
                    Puedo hacerlo
                </button>
            </div>
        `;

        this.showNotification(notification);

        // Mentor encouragement
        if (window.DetectiveMentor) {
            window.DetectiveMentor.showEncouraging();
        }
    },

    /**
     * Show tutorial suggestion
     * @param {string} errorType - Type of repeated error
     */
    showTutorialSuggestion(errorType) {
        const message = this.getTutorialMessage();
        if (!message) return;

        const notification = document.createElement('div');
        notification.className = 'streak-notification tutorial-notification';
        notification.innerHTML = `
            <div class="streak-icon">💡</div>
            <div class="streak-text">
                <div class="streak-title">¡Idea!</div>
                <div class="streak-subtitle">${message}</div>
            </div>
            <div class="streak-actions">
                <button class="btn btn-primary btn-small" onclick="RecommendationEngine.dismissNotification(this)">
                    Vale
                </button>
            </div>
        `;

        this.showNotification(notification);
    },

    /**
     * Show notification in UI
     */
    showNotification(element) {
        // Remove existing notifications
        document.querySelectorAll('.streak-notification').forEach(n => n.remove());

        // Add to page
        document.body.appendChild(element);

        // Animate in
        setTimeout(() => element.classList.add('visible'), 10);

        // Auto-dismiss after 10 seconds
        setTimeout(() => {
            if (element.parentElement) {
                element.classList.remove('visible');
                setTimeout(() => element.remove(), 300);
            }
        }, 10000);
    },

    /**
     * Dismiss notification
     */
    dismissNotification(button) {
        const notification = button.closest('.streak-notification');
        if (notification) {
            notification.classList.remove('visible');
            setTimeout(() => notification.remove(), 300);
        }
    },

    /**
     * Accept harder challenge suggestion
     */
    acceptHarderChallenge() {
        this.dismissNotification(document.querySelector('.streak-notification button'));
        console.log('🎯 User accepted harder challenge');

        // Logic: Increase Difficulty
        if (typeof store !== 'undefined' && store) {
            store.config = store.config || {};
            const currentRange = store.config.numberRange || 50;
            if (currentRange < 100) {
                store.config.numberRange = 100;
                if (window.Swal) {
                    Swal.fire({
                        title: '¡Nivel Subido!',
                        text: 'Ahora los números llegarán hasta el 100.',
                        icon: 'info',
                        timer: 2000,
                        showConfirmButton: false
                    });
                }
            }
        }

        if (window.DetectiveMentor) {
            window.DetectiveMentor.onCorrectAnswer(); // Celebration
        }
    },

    /**
     * Accept easier challenge suggestion
     */
    acceptEasierChallenge() {
        this.dismissNotification(document.querySelector('.streak-notification button'));
        console.log('🎯 User accepted easier challenge');

        // Logic: Reduce Difficulty
        if (typeof store !== 'undefined' && store) {
            store.config = store.config || {};
            const currentRange = store.config.numberRange || 50;
            if (currentRange > 10) {
                store.config.numberRange = 20; // Reduce execution
                store.config.allowCarrying = false; // Disable harder logic
                if (window.Swal) {
                    Swal.fire({
                        title: 'Dificultad Ajustada',
                        text: 'Vamos a practicar con números más pequeños un rato.',
                        icon: 'info',
                        timer: 2000,
                        showConfirmButton: false
                    });
                }
            }
        }

        // Reset failure counter
        this.state.consecutiveFailures = 0;

        if (window.DetectiveMentor) {
            window.DetectiveMentor.showThinking(); // Empathetic state
        }
    },

    /**
     * Get session statistics
     * @returns {Object} Session stats
     */
    getSessionStats() {
        const duration = Date.now() - (this.state.sessionStartTime || Date.now());
        return {
            totalAttempts: this.state.totalAttempts,
            correctAttempts: this.state.correctAttempts,
            accuracy: this.state.totalAttempts > 0
                ? Math.round((this.state.correctAttempts / this.state.totalAttempts) * 100)
                : 0,
            currentStreak: this.state.consecutiveSuccesses,
            sessionDuration: Math.round(duration / 60000)  // minutes
        };
    },

    /**
     * Reset state (for new session)
     */
    reset() {
        this.state = {
            consecutiveFailures: 0,
            consecutiveSuccesses: 0,
            totalAttempts: 0,
            correctAttempts: 0,
            errorHistory: [],
            lastRecommendation: null,
            sessionStartTime: Date.now()
        };
    }
};

// Expose globally
window.RecommendationEngine = RecommendationEngine;

// Initialize on load
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => RecommendationEngine.init());
} else {
    RecommendationEngine.init();
}

console.log('🎯 recommendationEngine.js loaded');
